import {syntaxTree} from '@codemirror/language';
import type {
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';
import type {CompletionData} from './types';

/**
 * Completion source for JavaScript contexts.
 * Suggests CSS class names inside string literals following common DOM API patterns.
 */
export function jsCompletionSource(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  const {state, pos} = context;
  const tree = syntaxTree(state);
  const node = tree.resolveInner(pos, -1);

  const isInString = node.name === 'String' || node.name === 'TemplateString';
  if (!isInString) return null;

  const stringStart = node.from;
  const lookbackStart = Math.max(0, stringStart - 60);
  const textBefore = state.doc.sliceString(lookbackStart, stringStart);

  if (
    /classList\.(add|remove|toggle|contains|replace)\s*\(\s*['"`]?$/.test(
      textBefore
    )
  ) {
    return getClassCompletions(context, data);
  }

  if (/querySelector(All)?\s*\(\s*['"`]?$/.test(textBefore)) {
    return getSelectorCompletions(context, data);
  }

  if (/getElementsByClassName\s*\(\s*['"`]?$/.test(textBefore)) {
    return getClassCompletions(context, data);
  }

  return null;
}

function getClassCompletions(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  const word = context.matchBefore(/[\w-]*/);
  if (!word) return null;

  const options = data.cssClasses.map((cls) => ({
    label: cls.className,
    displayLabel:
      cls.displayName !== cls.className ? cls.displayName : undefined,
    type: 'class' as const,
  }));

  if (options.length === 0) return null;

  return {from: word.from, options, validFor: /^[\w-]*$/};
}

function getSelectorCompletions(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  const word = context.matchBefore(/[\w.-]*/);
  if (!word) return null;

  const options = data.cssClasses.map((cls) => ({
    label: `.${cls.className}`,
    displayLabel: cls.displayName,
    type: 'class' as const,
  }));
  if (options.length === 0) return null;

  return {from: word.from, options, validFor: /^[\w.-]*$/};
}
